import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import {
  signInWithEmailAndPassword, signInWithCustomToken,
  sendPasswordResetEmail, onIdTokenChanged,
  signOut as firebaseSignOut, type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase/config';

interface CustomerAuthState {
  user: User | null;
  customerId: string | null;
  loading: boolean;
  error: string | null;
  otpPending: boolean;
  otpEmail: string | null;
  mustChangePassword: boolean;
  onboardingComplete: boolean;
}

interface CustomerAuthContextType extends CustomerAuthState {
  login: (email: string, password: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  clearError: () => void;
  clearOtp: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | null>(null);

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const loginInProgress = useRef(false);
  const [state, setState] = useState<CustomerAuthState>({
    user: null,
    customerId: null,
    loading: true,
    error: null,
    otpPending: false,
    otpEmail: null,
    mustChangePassword: false,
    // Default false: until we've explicitly read a `true` claim, treat
    // onboarding as incomplete. Keeps CustomerProtectedRoute from flashing
    // the portal for a new customer whose claim hasn't propagated yet.
    // `loading: true` (above) blocks redirect during initial read.
    onboardingComplete: false,
  });

  useEffect(() => {
    if (!auth) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }
    // onIdTokenChanged (not onAuthStateChanged) so we catch token refreshes —
    // critical for picking up new custom claims like `onboardingComplete`
    // after the setup wizard flips them server-side and force-refreshes.
    // Without this, the cached `false` claim sticks around and protected
    // routes briefly bounce to /portal/setup, showing the wizard's
    // "Verifying purchase..." spinner before settling.
    const unsub = onIdTokenChanged(auth, async (user) => {
      // Skip auth state changes during login flow (prevents premature redirect)
      if (loginInProgress.current) return;
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        const customerId = (tokenResult.claims.customerId as string) || null;
        const mustChangePassword = !!(tokenResult.claims.mustChangePassword);
        // Strict: only `true` counts as complete. `undefined` means the
        // claim hasn't been set yet (new signup) and must go through setup.
        const onboardingComplete = tokenResult.claims.onboardingComplete === true;
        if (customerId) {
          setState({ user, customerId, loading: false, error: null, otpPending: false, otpEmail: null, mustChangePassword, onboardingComplete });
        } else {
          setState(prev => ({ ...prev, loading: false }));
        }
      } else {
        setState(prev => ({ ...prev, user: null, customerId: null, loading: false, onboardingComplete: false }));
      }
    });
    return unsub;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (!auth) throw new Error('Firebase not configured');
    setState(prev => ({ ...prev, error: null, loading: true }));
    // Prevent onAuthStateChanged from updating state during login
    // (avoids premature redirect that loses local component state)
    loginInProgress.current = true;
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const tokenResult = await cred.user.getIdTokenResult();
      const customerId = (tokenResult.claims.customerId as string) || null;

      if (!customerId) {
        loginInProgress.current = false;
        await firebaseSignOut(auth);
        setState(prev => ({ ...prev, loading: false, error: 'This account does not have customer access.' }));
        return;
      }

      // Check if 2FA is required
      const token = await cred.user.getIdToken();
      let requiresOtp = false;
      try {
        const otpRes = await fetch('/api/customer/auth/send-otp', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (otpRes.ok) {
          const otpData = await otpRes.json();
          requiresOtp = !!otpData.requiresOtp;
        }
      } catch (otpErr) {
        // Network/server failure reaching the OTP endpoint. Fail open
        // (don't block login) but surface for debugging — silent swallow
        // here is how past auth-flow bugs went unnoticed.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[customer-auth] OTP preflight failed, continuing without 2FA:', otpErr);
        }
      }

      if (requiresOtp) {
        // Sign out until OTP verified — user is not fully authenticated yet
        await firebaseSignOut(auth);
        loginInProgress.current = false;
        setState(prev => ({ ...prev, loading: false, otpPending: true, otpEmail: email }));
      } else {
        loginInProgress.current = false;
        const mustChangePassword = !!(tokenResult.claims.mustChangePassword);
        const onboardingComplete = tokenResult.claims.onboardingComplete === true;
        setState({
          user: cred.user, customerId, loading: false, error: null,
          otpPending: false, otpEmail: null, mustChangePassword, onboardingComplete,
        });
      }
    } catch (err: unknown) {
      loginInProgress.current = false;
      const message = err instanceof Error ? err.message : 'Login failed';
      const friendly = message.includes('wrong-password') || message.includes('invalid-credential')
        ? 'Incorrect email or password.'
        : message.includes('user-not-found')
        ? 'No account found with that email.'
        : message.includes('too-many-requests')
        ? 'Too many attempts. Try again later.'
        : 'Login failed. Please try again.';
      setState(prev => ({ ...prev, loading: false, error: friendly }));
    }
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string) => {
    if (!auth) throw new Error('Firebase not configured');
    setState(prev => ({ ...prev, error: null, loading: true }));
    try {
      const res = await fetch('/api/customer/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setState(prev => ({ ...prev, loading: false, error: data.error || 'Invalid code.' }));
        return;
      }
      const cred = await signInWithCustomToken(auth, data.customToken);
      const tokenResult = await cred.user.getIdTokenResult(true);
      const customerId = (tokenResult.claims.customerId as string) || null;
      const mustChangePassword = !!(tokenResult.claims.mustChangePassword);
      const onboardingComplete = tokenResult.claims.onboardingComplete === true;
      if (!customerId) {
        await firebaseSignOut(auth);
        setState(prev => ({ ...prev, loading: false, error: 'No customer access.' }));
        return;
      }
      setState({ user: cred.user, customerId, loading: false, error: null, otpPending: false, otpEmail: null, mustChangePassword, onboardingComplete });
    } catch (verifyErr) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[customer-auth] verifyOtp failed:', verifyErr);
      }
      setState(prev => ({ ...prev, loading: false, error: 'Verification failed. Please try again.' }));
    }
  }, []);

  const logout = useCallback(async () => {
    if (auth) await firebaseSignOut(auth);
    setState({ user: null, customerId: null, loading: false, error: null, otpPending: false, otpEmail: null, mustChangePassword: false, onboardingComplete: false });
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    if (!auth) throw new Error('Firebase not configured');
    // Firebase sends the reset link to `${authDomain}/__/auth/action` which
    // is a Firebase-hosted page that handles the oobCode end-to-end. We do
    // not need a custom /portal/reset-password route in this app. If we
    // ever want the reset to land inside the portal we'd pass an
    // actionCodeSettings object here with `url` + `handleCodeInApp: true`
    // and build the handler UI.
    await sendPasswordResetEmail(auth, email);
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const clearOtp = useCallback(() => {
    setState(prev => ({ ...prev, otpPending: false, error: null }));
  }, []);

  return (
    <CustomerAuthContext.Provider value={{ ...state, login, verifyOtp, logout, resetPassword, clearError, clearOtp }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
}
